import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
    products: [],
    product: { reviews: [], user: {} },
    status: 'idle',
    error: null,
};

export const fetchProducts = createAsyncThunk('products/fetchProducts', async (arg = '') => {
    let keyword = '';
    let minPrice = 0;
    let maxPrice = 1000000;
    let category = '';
    let rating = 0;
    let user = '';
    let sort = '';

    if (typeof arg === 'string') {
        keyword = arg;
    } else {
        keyword = arg.keyword || '';
        minPrice = arg.minPrice || 0;
        maxPrice = arg.maxPrice || 1000000;
        category = arg.category || '';
        rating = arg.rating || 0;
        user = arg.user || '';
        sort = arg.sort || '';
    }

    let query = `?keyword=${keyword}&minPrice=${minPrice}&maxPrice=${maxPrice}&rating=${rating}`;
    if (category) query += `&category=${category}`;
    if (user) query += `&user=${user}`;
    if (sort) query += `&sort=${sort}`;

    const { data } = await axios.get(`/api/products${query}`);
    return data;
});

export const fetchMyProducts = createAsyncThunk('products/fetchMyProducts', async (_, { getState, rejectWithValue }) => {
    const { user: { userInfo } } = getState();

    if (!userInfo || !userInfo.token) {
        return rejectWithValue('Not authorized, no token');
    }

    const config = {
        headers: {
            Authorization: `Bearer ${userInfo.token}`,
        },
    };
    try {
        const { data } = await axios.get('/api/products/myproducts', config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});


export const fetchProductById = createAsyncThunk('products/fetchProductById', async (productId) => {
    const { data } = await axios.get(`/api/products/${productId}`);
    return data;
});

export const deleteProduct = createAsyncThunk('products/deleteProduct', async (productId, { getState, rejectWithValue }) => {
    const { user: { userInfo } } = getState();

    if (!userInfo || !userInfo.token) {
        return rejectWithValue('Not authorized, no token');
    }

    const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
    try {
        await axios.delete(`/api/products/${productId}`, config);
        return productId;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

export const createProduct = createAsyncThunk('products/createProduct', async (productData, { getState, rejectWithValue }) => {
    const { user: { userInfo } } = getState();

    if (!userInfo || !userInfo.token) {
        return rejectWithValue('Not authorized, no token');
    }

    const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
    try {
        const { data } = await axios.post('/api/products', productData, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

export const updateProduct = createAsyncThunk('products/updateProduct', async (productData, { getState, rejectWithValue }) => {
    const { user: { userInfo } } = getState();

    if (!userInfo || !userInfo.token) {
        return rejectWithValue('Not authorized, no token');
    }

    const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
    try {
        const { data } = await axios.put(`/api/products/${productData._id}`, productData, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

export const fetchFeedProducts = createAsyncThunk('products/fetchFeedProducts', async (_, { getState, rejectWithValue }) => {
    const { user: { userInfo } } = getState();

    if (!userInfo || !userInfo.token) {
        return rejectWithValue('Not authorized, no token');
    }

    const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
    try {
        const { data } = await axios.get('/api/products/feed', config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

export const productSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        resetProductDetails: (state) => {
            state.product = { reviews: [], user: {} };
            state.status = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(fetchProducts.fulfilled, (state, action) => { state.status = 'succeeded'; state.products = action.payload; })
            .addCase(fetchProducts.rejected, (state, action) => { state.status = 'failed'; state.error = action.error.message; })
            .addCase(fetchMyProducts.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(fetchMyProducts.fulfilled, (state, action) => { state.status = 'succeeded'; state.products = action.payload; })
            .addCase(fetchMyProducts.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload || action.error.message; })
            .addCase(fetchProductById.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(fetchProductById.fulfilled, (state, action) => { state.status = 'succeeded'; state.product = action.payload; })
            .addCase(fetchProductById.rejected, (state, action) => { state.status = 'failed'; state.error = action.error.message; })
            .addCase(deleteProduct.fulfilled, (state, action) => { state.products = state.products.filter((p) => p._id !== action.payload); })
            .addCase(createProduct.fulfilled, (state, action) => { state.product = action.payload; })
            .addCase(updateProduct.fulfilled, (state, action) => { state.product = action.payload; })
            .addCase(fetchFeedProducts.pending, (state) => { state.status = 'loading'; state.error = null; })
            .addCase(fetchFeedProducts.fulfilled, (state, action) => { state.status = 'succeeded'; state.products = action.payload; })
            .addCase(fetchFeedProducts.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload || action.error.message; });
    },
});

export const { resetProductDetails } = productSlice.actions;
export default productSlice.reducer;